import { useEffect, useState, useRef } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import PolotnoSDK from './sdk/PolotnoSDK';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Download, Upload, FileJson, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const SAMPLE_JSON = {
  "width": 1920,
  "height": 1080,
  "fonts": ["Archivo Black", "Roboto"],
  "pages": [
    {
      "id": "page-1",
      "children": [
        {
          "id": "bg-1",
          "type": "rect",
          "x": 0,
          "y": 0,
          "width": 1920,
          "height": 1080,
          "fill": "#00A67E",
          "visible": true
        },
        {
          "id": "text-1",
          "type": "text",
          "x": 960,
          "y": 400,
          "width": 800,
          "height": 200,
          "text": "SDK Polotno",
          "fontSize": 120,
          "fontFamily": "Archivo Black",
          "fontWeight": "bold",
          "fill": "#FFFFFF",
          "align": "center",
          "visible": true,
          "shadowEnabled": true,
          "shadowColor": "black",
          "shadowBlur": 10
        },
        {
          "id": "text-2",
          "type": "text",
          "x": 960,
          "y": 600,
          "width": 600,
          "height": 100,
          "text": "Editor de Design Completo",
          "fontSize": 40,
          "fontFamily": "Roboto",
          "fill": "#FFEF44",
          "align": "center",
          "visible": true
        }
      ],
      "background": "white"
    }
  ]
};

const Home = () => {
  const [sdk, setSdk] = useState(null);
  const [jsonInput, setJsonInput] = useState(JSON.stringify(SAMPLE_JSON, null, 2));
  const [exportedImage, setExportedImage] = useState(null);
  const [fonts, setFonts] = useState([]);
  const canvasRef = useRef(null);

  useEffect(() => {
    const polotnoSDK = new PolotnoSDK('canvas-container', {
      width: 1920,
      height: 1080
    });
    setSdk(polotnoSDK);

    polotnoSDK.loadJSON(SAMPLE_JSON);

    loadFonts(polotnoSDK);

    return () => {
      polotnoSDK.destroy();
    };
  }, []);

  const loadFonts = async (sdkInstance) => {
    try {
      const fontsList = await sdkInstance.getFonts();
      setFonts(fontsList);
    } catch (error) {
      console.error('Erro ao carregar fontes:', error);
    }
  };

  const handleLoadJSON = async () => {
    try {
      const jsonData = JSON.parse(jsonInput);
      await sdk.loadJSON(jsonData);
      toast.success('JSON carregado com sucesso!');
    } catch (error) {
      toast.error('Erro ao carregar JSON: ' + error.message);
    }
  };

  const handleExportJSON = () => {
    try {
      const json = sdk.exportJSON();
      setJsonInput(json);
      toast.success('JSON exportado com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar JSON: ' + error.message);
    }
  };

  const handleExportImage = async (format = 'png') => {
    try {
      const imageURL = await sdk.exportImage(format, 1);
      setExportedImage(imageURL);
      toast.success(`Imagem ${format.toUpperCase()} gerada com sucesso!`);
    } catch (error) {
      toast.error('Erro ao exportar imagem: ' + error.message);
    }
  };

  const handleDownloadImage = () => {
    if (!exportedImage) return;
    const link = document.createElement('a');
    link.download = 'polotno-export.png';
    link.href = exportedImage;
    link.click();
  };

  const handleImageUpload = async (event) => {
    try {
      const file = event.target.files[0];
      if (!file) return;

      const imageUrl = await sdk.uploadImage(file);
      
      sdk.addElement({
        type: 'image',
        x: 500,
        y: 300,
        width: 400,
        height: 400,
        src: imageUrl,
        visible: true
      });

      toast.success('Imagem enviada e adicionada ao canvas!');
    } catch (error) {
      toast.error('Erro ao enviar imagem: ' + error.message);
    }
  };

  const handleAddText = () => {
    try {
      sdk.addElement({
        type: 'text',
        x: Math.random() * 1000 + 200,
        y: Math.random() * 500 + 200,
        width: 400,
        height: 100,
        text: 'Novo Texto',
        fontSize: 48,
        fontFamily: 'Roboto',
        fill: '#000000',
        align: 'center',
        visible: true
      });
      toast.success('Texto adicionado!');
    } catch (error) {
      toast.error('Erro ao adicionar texto: ' + error.message);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-4" data-testid="main-title">
            Polotno SDK
          </h1>
          <p className="text-lg text-gray-600" data-testid="subtitle">
            Editor de Design Completo em JavaScript
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Card data-testid="canvas-card">
              <CardHeader>
                <CardTitle>Canvas de Edição</CardTitle>
                <CardDescription>Visualize e edite seu design</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-100 rounded-lg p-4 mb-4">
                  <div 
                    id="canvas-container" 
                    ref={canvasRef}
                    className="w-full bg-white rounded shadow-lg"
                    style={{ minHeight: '400px' }}
                    data-testid="canvas-container"
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={handleAddText} data-testid="add-text-btn">
                    Adicionar Texto
                  </Button>
                  <label>
                    <Button as="span" variant="outline" data-testid="upload-image-btn">
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Imagem
                    </Button>
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleImageUpload} 
                      className="hidden"
                    />
                  </label>
                </div>
              </CardContent>
            </Card>

            {exportedImage && (
              <Card className="mt-6" data-testid="export-preview-card">
                <CardHeader>
                  <CardTitle>Imagem Exportada</CardTitle>
                </CardHeader>
                <CardContent>
                  <img src={exportedImage} alt="Exported" className="w-full rounded-lg shadow" data-testid="exported-image" />
                  <Button onClick={handleDownloadImage} className="mt-4" data-testid="download-btn">
                    <Download className="w-4 h-4 mr-2" />
                    Baixar Imagem
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>

          <div>
            <Card data-testid="controls-card">
              <CardHeader>
                <CardTitle>Controles</CardTitle>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="json" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="json" data-testid="json-tab">JSON</TabsTrigger>
                    <TabsTrigger value="export" data-testid="export-tab">Exportar</TabsTrigger>
                  </TabsList>
                  <TabsContent value="json" className="space-y-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">
                        JSON do Polotno
                      </label>
                      <Textarea
                        value={jsonInput}
                        onChange={(e) => setJsonInput(e.target.value)}
                        className="font-mono text-xs h-96"
                        placeholder="Cole seu JSON aqui..."
                        data-testid="json-textarea"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={handleLoadJSON} className="flex-1" data-testid="load-json-btn">
                        <FileJson className="w-4 h-4 mr-2" />
                        Carregar JSON
                      </Button>
                      <Button onClick={handleExportJSON} variant="outline" className="flex-1" data-testid="export-json-btn">
                        Exportar JSON
                      </Button>
                    </div>
                  </TabsContent>
                  <TabsContent value="export" className="space-y-4">
                    <div className="space-y-3">
                      <Button 
                        onClick={() => handleExportImage('png')} 
                        className="w-full"
                        data-testid="export-png-btn"
                      >
                        <ImageIcon className="w-4 h-4 mr-2" />
                        Exportar PNG
                      </Button>
                      <Button 
                        onClick={() => handleExportImage('jpeg')} 
                        variant="outline"
                        className="w-full"
                        data-testid="export-jpeg-btn"
                      >
                        <ImageIcon className="w-4 h-4 mr-2" />
                        Exportar JPEG
                      </Button>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>

            <Card className="mt-6" data-testid="fonts-card">
              <CardHeader>
                <CardTitle>Fontes Disponíveis</CardTitle>
                <CardDescription>{fonts.length} fontes carregadas</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {fonts.slice(0, 10).map((font, index) => (
                    <div key={index} className="text-sm p-2 bg-gray-50 rounded" data-testid={`font-item-${index}`}>
                      {font.family}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="mt-6" data-testid="docs-card">
              <CardHeader>
                <CardTitle>Documentação</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-2 text-gray-600">
                <p><strong>loadJSON(json)</strong> - Carrega JSON do Polotno</p>
                <p><strong>exportJSON()</strong> - Exporta JSON atual</p>
                <p><strong>exportImage(format)</strong> - Exporta como imagem</p>
                <p><strong>addElement(element)</strong> - Adiciona elemento</p>
                <p><strong>updateElement(id, updates)</strong> - Atualiza elemento</p>
                <p><strong>removeElement(id)</strong> - Remove elemento</p>
                <p><strong>uploadImage(file)</strong> - Envia imagem</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;